import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { requestRecommendations } from "../../../utils/requestRecommendations";
import { levelDisplayNames } from "../../../utils/displayNameUtils";
import { getRiskLevelPriorityColors } from "../../../utils/riskLevelUtils";
import { storeRcRecommendations, getStoredRcRecommendations } from "../../../utils/recsCache";

const sectionCardShellStyle = {
  background: "linear-gradient(145deg, var(--card-bg), rgba(59,130,246,0.05))",
  borderRadius: "16px",
  border: "1px solid var(--card-border)",
  boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
};

const SectionCardHeader = ({ title, icon }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
    {icon && (
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "12px",
          background: "rgba(59,130,246,0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "20px",
          color: "#3b82f6",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
    )}
    {title && (
      <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
    )}
  </div>
);

const SectionCard = ({ children, title, icon, style }) => (
  <div
    style={{
      ...sectionCardShellStyle,
      padding: "24px",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      ...style,
    }}
  >
    {(title || icon) && <SectionCardHeader title={title} icon={icon} />}
    {children}
  </div>
);

const SectionCardAccordion = ({ children, title, icon, style, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div style={{ ...sectionCardShellStyle, overflow: "hidden", ...style }}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "24px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <SectionCardHeader title={title} icon={icon} />
        <i
          className={`fas fa-chevron-${isOpen ? "up" : "down"}`}
          style={{ color: "#64748b", fontSize: "14px", flexShrink: 0 }}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "0 24px 24px",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const StatTile = ({ label, value, color = "#2563eb", bg = "rgba(59, 130, 246, 0.08)", border = "rgba(59, 130, 246, 0.2)" }) => (
  <div
    style={{
      background: bg,
      borderRadius: "8px",
      padding: "12px",
      textAlign: "center",
      border: `1px solid ${border}`,
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontSize: "10px",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color,
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--card-text)", marginTop: "4px", wordBreak: "break-word" }}>
      {value}
    </div>
  </div>
);

const Badge = ({ label, riskLevel }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "recs.regComp" });
  const style =
    riskLevel === "not_tested"
      ? { gradient: "#6b7280", text: "white" }
      : getRiskLevelPriorityColors(riskLevel);
  const display =
    label === "not_tested" || riskLevel === "not_tested"
      ? t("notTested")
      : levelDisplayNames[label] || levelDisplayNames[riskLevel] || label;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 10px",
        borderRadius: "999px",
        fontSize: "11px",
        fontWeight: 600,
        color: "white",
        background: style.gradient || style.bg,
        whiteSpace: "nowrap",
      }}
    >
      {display}
    </span>
  );
};

const formatNumber = (value, digits = 1, t) => {
  if (typeof value !== "number" || Number.isNaN(value)) return t("notAvailable");
  return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(digits);
};

const formatPercent = (value, t) => {
  if (typeof value !== "number" || Number.isNaN(value)) return t("notAvailable");
  return `${value.toFixed(1)}${t("percentUnit")}`;
};

const formatSeconds = (value, t) => {
  if (typeof value !== "number" || Number.isNaN(value)) return t("notAvailable");
  return `${value.toFixed(3)}${t("secondsUnit")}`;
};

const formatRiskDistribution = (distribution, t) => {
  if (!distribution || typeof distribution !== "object") return t("notAvailable");
  const entries = Object.entries(distribution);
  if (entries.length === 0) return t("notAvailable");
  return entries
    .map(([level, count]) => `${levelDisplayNames[level] || level}: ${count}`)
    .join(", ");
};

const tableHeaderStyle = {
  padding: "14px 16px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--muted-text)",
  background: "rgba(59, 130, 246, 0.08)",
  borderBottom: "1px solid var(--card-border)",
  whiteSpace: "nowrap",
};

const InfoBlock = ({ label, children, accent = "#3b82f6" }) => (
  <div
    style={{
      background: "rgba(0, 0, 0, 0.04)",
      borderRadius: "12px",
      padding: "16px",
      border: "1px solid var(--card-border)",
    }}
  >
    {label && (
      <div
        style={{
          fontSize: "12px",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: accent,
          marginBottom: "10px",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
    )}
    <div style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.5 }}>{children}</div>
  </div>
);

const ItemCard = ({ title, badge, meta, children, icon = "fa-exclamation-triangle" }) => (
  <div
    style={{
      border: "1px solid var(--card-border)",
      borderRadius: "14px",
      overflow: "hidden",
      background: "var(--card-bg)",
    }}
  >
    <div
      style={{
        padding: "16px 20px",
        background: "rgba(0, 0, 0, 0.02)",
        borderBottom: "1px solid var(--card-border)",
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
      }}
    >
      <div
        style={{
          width: "32px",
          height: "32px",
          borderRadius: "8px",
          background: "rgba(0, 0, 0, 0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <i className={`fas ${icon}`} style={{ fontSize: "14px", color: "var(--card-text)", opacity: 0.7 }} />
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>{title}</span>
          {badge}
        </div>
        {meta}
      </div>
    </div>
    <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>{children}</div>
  </div>
);

const DataTable = ({ columns, rows, emptyMessage }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "recs.regComp" });
  const resolvedEmptyMessage = emptyMessage ?? t("noDataAvailable");
  if (!rows || rows.length === 0) {
    return <div style={{ color: "#64748b", fontSize: "14px" }}>{resolvedEmptyMessage}</div>;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: 0,
          fontSize: "14px",
          minWidth: columns.length > 5 ? "900px" : undefined,
        }}
      >
        <thead>
          <tr>
            {columns.map((col, colIdx) => (
              <th
                key={col.key}
                style={{
                  ...tableHeaderStyle,
                  ...(colIdx === 0 ? { borderTopLeftRadius: "10px" } : {}),
                  ...(colIdx === columns.length - 1 ? { borderTopRightRadius: "10px" } : {}),
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const isLast = idx === rows.length - 1;
            return (
              <tr
                key={row._key || idx}
                style={{ background: idx % 2 === 0 ? "transparent" : "rgba(59, 130, 246, 0.03)" }}
              >
                {columns.map((col, colIdx) => (
                  <td
                    key={col.key}
                    style={{
                      padding: "14px 16px",
                      borderBottom: isLast ? "none" : "1px solid var(--card-border)",
                      verticalAlign: "top",
                      color: "var(--card-text)",
                      lineHeight: 1.5,
                      ...(isLast && colIdx === 0 ? { borderBottomLeftRadius: "10px" } : {}),
                      ...(isLast && colIdx === columns.length - 1 ? { borderBottomRightRadius: "10px" } : {}),
                    }}
                  >
                    {typeof col.render === "function" ? col.render(row, idx) : (row[col.key] ?? t("notAvailable"))}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const SubTitle = ({ children }) => (
  <div
    style={{
      fontSize: "12px",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: "var(--muted-text)",
      marginBottom: "12px",
      fontWeight: 600,
    }}
  >
    {children}
  </div>
);

const StandardSection = ({ standard }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "recs.regComp" });
  const totals = standard.computed_totals || {};
  const inputTotals = standard.input_totals || {};
  const recommendations = standard.recommendations || {};

  const riskDistRows = recommendations.risk_distribution
    ? Object.entries(recommendations.risk_distribution).map(([level, count]) => ({
      _key: level,
      level,
      count,
    }))
    : [];

  return (
    <div
      style={{
        border: "2px solid var(--card-border)",
        borderRadius: "20px",
        padding: "20px",
        background: "rgba(59, 130, 246, 0.03)",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px" }}>
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "12px",
            background: "rgba(59,130,246,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "20px",
            color: "#3b82f6",
            flexShrink: 0,
          }}
        >
          <i className="fas fa-shield-alt" />
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px" }}>
          <h3
            style={{
              margin: 0,
              color: "var(--card-text)",
              fontSize: "20px",
              fontWeight: 700,
            }}
          >
            {standard.standard}
          </h3>
          {standard.standard_key && (
            <div style={{ fontSize: "13px", color: "#94a3b8", fontWeight: 500 }}>
              {standard.standard_key}
            </div>
          )}
        </div>
      </div>

      <SectionCardAccordion title={t("computedTotals")} icon={<i className="fas fa-chart-bar" />}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "10px",
          }}
        >
          <StatTile label={t("totalTests")} value={formatNumber(totals.total_tests, 0, t)} />
          <StatTile
            label={t("failed")}
            value={formatNumber(totals.failed_tests, 0, t)}
            color="#dc2626"
            bg="rgba(239, 68, 68, 0.08)"
            border="rgba(239, 68, 68, 0.2)"
          />
          <StatTile
            label={t("passed")}
            value={formatNumber(totals.passed_tests, 0, t)}
            color="#16a34a"
            bg="rgba(34, 197, 94, 0.08)"
            border="rgba(34, 197, 94, 0.2)"
          />
          <StatTile
            label={t("readiness")}
            value={formatPercent(totals.readiness_percent, t)}
            color="#7c3aed"
            bg="rgba(139, 92, 246, 0.08)"
            border="rgba(139, 92, 246, 0.2)"
          />
          <StatTile
            label={t("avgResponse")}
            value={formatSeconds(totals.avg_response_time, t)}
            color="#d97706"
            bg="rgba(245, 158, 11, 0.08)"
            border="rgba(245, 158, 11, 0.2)"
          />
        </div>
      </SectionCardAccordion>

      {Object.keys(inputTotals).length > 0 && (
        <SectionCardAccordion title={t("inputTotals")} icon={<i className="fas fa-database" />}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: "10px",
            }}
          >
            <StatTile label={t("totalTests")} value={formatNumber(inputTotals.total_tests, 0, t)} />
            <StatTile
              label={t("failed")}
              value={formatNumber(inputTotals.failed_tests, 0, t)}
              color="#dc2626"
              bg="rgba(239, 68, 68, 0.08)"
              border="rgba(239, 68, 68, 0.2)"
            />
            <StatTile
              label={t("readiness")}
              value={formatPercent(inputTotals.readiness_percent, t)}
              color="#7c3aed"
              bg="rgba(139, 92, 246, 0.08)"
              border="rgba(139, 92, 246, 0.2)"
            />
            <StatTile
              label={t("avgResponse")}
              value={formatSeconds(inputTotals.avg_response_time, t)}
              color="#d97706"
              bg="rgba(245, 158, 11, 0.08)"
              border="rgba(245, 158, 11, 0.2)"
            />
          </div>
        </SectionCardAccordion>
      )}

      <SectionCardAccordion title={t("summary")} icon={<i className="fas fa-table" />}>
        <DataTable
          columns={[
            {
              key: "standard",
              label: t("standard"),
              render: (row) => row.standard || standard.standard || t("notAvailable"),
            },
            { key: "applicable_section", label: t("section") },
            { key: "compliance_subcategory", label: t("subcategory") },
            {
              key: "total_tests",
              label: t("total"),
              render: (row) => formatNumber(row.total_tests, 0, t),
            },
            {
              key: "failed_prompts",
              label: t("failed"),
              render: (row) => formatNumber(row.failed_prompts, 0, t),
            },
            {
              key: "passed_prompts",
              label: t("passed"),
              render: (row) => formatNumber(row.passed_prompts, 0, t),
            },
            {
              key: "failure_rate",
              label: t("failureRate"),
              render: (row) => formatPercent(row.failure_rate, t),
            },
          ]}
          rows={standard.summary_table || []}
          emptyMessage={t("noSectionSummary")}
        />
      </SectionCardAccordion>



      <SectionCardAccordion title={t("recommendations")} icon={<i className="fas fa-lightbulb" />}>
        {recommendations.overall_recommendation && (
          <div style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.5, marginTop: "-4px" }}>
            {recommendations.overall_recommendation}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {riskDistRows.length > 0 && (
            <div>
              <SubTitle>{t("riskDistribution")}</SubTitle>
              <DataTable
                columns={[
                  {
                    key: "level",
                    label: t("riskLevel"),
                    render: (row) => <Badge label={row.level} riskLevel={row.level} />,
                  },
                  { key: "count", label: t("count"), render: (row) => formatNumber(row.count, 0, t) },
                ]}
                rows={riskDistRows}
              />
            </div>
          )}

          {Array.isArray(recommendations.highest_failure_subsections) &&
            recommendations.highest_failure_subsections.length > 0 && (
              <div>
                <SubTitle>{t("highestFailureSubsections")}</SubTitle>
                <DataTable
                  columns={[
                    { key: "applicable_section", label: t("section") },
                    { key: "compliance_subcategory", label: t("subcategory") },
                    {
                      key: "failed_prompt_count",
                      label: t("failed"),
                      render: (row) => formatNumber(row.failed_prompt_count, 0, t),
                    },
                    {
                      key: "failure_rate",
                      label: t("failureRate"),
                      render: (row) => formatPercent(row.failure_rate, t),
                    },
                  ]}
                  rows={recommendations.highest_failure_subsections}
                />
              </div>
            )}

          {Array.isArray(recommendations.subsection_recommendations) &&
            recommendations.subsection_recommendations.length > 0 && (
              <div>
                <SubTitle>{t("subsectionRecommendations")}</SubTitle>
                <DataTable
                  columns={[
                    { key: "applicable_section", label: t("section") },
                    { key: "compliance_subcategory", label: t("subcategory") },
                    {
                      key: "priority",
                      label: t("priority"),
                      render: (row) => <Badge label={row.priority} riskLevel={row.priority} />,
                    },
                    {
                      key: "failed_prompt_count",
                      label: t("failed"),
                      render: (row) => formatNumber(row.failed_prompt_count, 0, t),
                    },
                    {
                      key: "failure_rate",
                      label: t("failureRate"),
                      render: (row) => formatPercent(row.failure_rate, t),
                    },
                    {
                      key: "recommendation",
                      label: t("recommendation"),
                      render: (row) => row.recommendation || t("notAvailable"),
                    },
                  ]}
                  rows={recommendations.subsection_recommendations}
                />
              </div>
            )}
        </div>
      </SectionCardAccordion>

      <SectionCardAccordion title={t("failurePatterns")} icon={<i className="fas fa-project-diagram" />}>
        {(standard.failure_patterns || []).length === 0 ? (
          <div style={{ color: "#64748b", fontSize: "14px" }}>{t("noFailurePatterns")}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
            {(standard.failure_patterns || []).map((pattern, idx) => (
              <ItemCard
                key={pattern.representative_test_id || idx}
                title={pattern.representative_test_id || `${t("pattern")} ${idx + 1}`}
                icon="fa-bug"
                meta={
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    {pattern.applicable_section || t("notAvailable")}
                    {pattern.compliance_subcategory ? ` · ${pattern.compliance_subcategory}` : ""}
                  </div>
                }
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
                    gap: "10px",
                  }}
                >
                  <StatTile
                    label={t("failedPromptCount")}
                    value={formatNumber(pattern.failed_prompt_count, 0, t)}
                    color="#dc2626"
                    bg="rgba(239, 68, 68, 0.08)"
                    border="rgba(239, 68, 68, 0.2)"
                  />
                </div>
                {pattern.risk_distribution && (
                  <InfoBlock label={t("riskDistribution")}>
                    {formatRiskDistribution(pattern.risk_distribution, t)}
                  </InfoBlock>
                )}
                <InfoBlock label={t("failurePattern")}>{pattern.failure_pattern || t("notAvailable")}</InfoBlock>
                {pattern.recommended_correction && (
                  <InfoBlock label={t("recommendedCorrection")} accent="#16a34a">
                    {pattern.recommended_correction}
                  </InfoBlock>
                )}
                {pattern.representative_failed_prompt && (
                  <InfoBlock label={t("representativeFailedPrompt")}>{pattern.representative_failed_prompt}</InfoBlock>
                )}
                {pattern.representative_response && (
                  <InfoBlock label={t("representativeResponse")}>{pattern.representative_response}</InfoBlock>
                )}
              </ItemCard>
            ))}
          </div>
        )}
      </SectionCardAccordion>

      <SectionCardAccordion
        title={t("evidence")}
        icon={<i className="fas fa-file-alt" />}
      >
        {standard.evidence_table_note && (
          <div style={{ fontSize: "13px", color: "#64748b", lineHeight: 1.5, marginTop: "-4px" }}>
            {standard.evidence_table_note}
          </div>
        )}
        {(standard.evidence_table || []).length === 0 ? (
          <div style={{ color: "#64748b", fontSize: "14px" }}>{t("noEvidenceRows")}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
            {(standard.evidence_table || []).map((row, idx) => (
              <ItemCard
                key={row.test_id || idx}
                title={row.test_id || `${t("evidence")} ${idx + 1}`}
                badge={<Badge label={row.risk_level} riskLevel={row.risk_level} />}
                icon="fa-search"
                meta={
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", fontSize: "12px", color: "#6b7280" }}>
                    <span>
                      {row.applicable_section}
                      {row.compliance_subcategory ? ` · ${row.compliance_subcategory}` : ""}
                      {row.response_time != null ? ` · ${formatSeconds(row.response_time, t)}` : ""}
                    </span>
                  </div>
                }
              >
                {row.test_methodology && (
                  <InfoBlock label={t("testMethodology")}>{row.test_methodology}</InfoBlock>
                )}
                <InfoBlock label={t("failedPrompt")}>{row.failed_prompt || t("notAvailable")}</InfoBlock>
                <InfoBlock label={t("llmAgentResponse")}>{row.llm_or_agent_response || t("notAvailable")}</InfoBlock>
                <InfoBlock label={t("reasonTestFailed")} accent="#ef4444">
                  {row.reason_test_failed || t("notAvailable")}
                </InfoBlock>
                {row.potential_correction && (
                  <InfoBlock label={t("potentialCorrection")} accent="#16a34a">
                    {row.potential_correction}
                  </InfoBlock>
                )}
                {row.raw_failure_indicators && (
                  <InfoBlock label={t("rawFailureIndicators")}>{row.raw_failure_indicators}</InfoBlock>
                )}
                {Array.isArray(row.matched_failure_indicators) && (
                  <InfoBlock label={t("matchedFailureIndicators")}>
                    {row.matched_failure_indicators.length > 0
                      ? row.matched_failure_indicators.join(", ")
                      : t("na")}
                  </InfoBlock>
                )}
              </ItemCard>
            ))}
          </div>
        )}
      </SectionCardAccordion>

      <SectionCardAccordion title={t("subcategoriesNotTested")} icon={<i className="fas fa-ban" />}>
        <DataTable
          columns={[
            { key: "applicable_section", label: t("section") },
            { key: "compliance_subcategory", label: t("subcategory") },
          ]}
          rows={standard.not_tested_subcategories || []}
          emptyMessage={t("na")}
        />
      </SectionCardAccordion>

      <SectionCardAccordion title={t("validationWarnings")} icon={<i className="fas fa-exclamation-triangle" />}>
        <div style={{ fontSize: "14px", color: "#64748b" }}>{t("na")}</div>
      </SectionCardAccordion>
    </div>
  );
};

const RegulationComplianceRecs = ({ reportData }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "recs.regComp" });
  const [recommendationDetails, setRecommendationDetails] = useState(() => {
    const reportId = reportData?.id;
    return reportId ? getStoredRcRecommendations(reportId) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const reportId = reportData?.id;
    if (!reportId) {
      setRecommendationDetails(null);
      return;
    }

    const stored = getStoredRcRecommendations(reportId);
    setRecommendationDetails(stored || null);
  }, [reportData?.id]);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get report ID from reportData
      const reportId = reportData?.id;
      if (!reportId) {
        throw new Error(t("reportIdNotFound"));
      }

      const result = await requestRecommendations({
        recommFor: "reg",
        reportId,
        reportData,
        fallbackMessage: t("noRecommendationsAvailable"),
      });
      setRecommendationDetails(result);

      if (result && Object.keys(result).length > 0) {
        storeRcRecommendations(reportId, result);
      }
    } catch (err) {
      console.error("Error fetching recommendations:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!recommendationDetails && !loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 40px",
          background: "var(--card-bg)",
          borderRadius: "12px",
          border: "1px solid var(--card-border)",
          marginTop: "20px",
        }}
      >
        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "16px",
            background: "rgba(59, 130, 246, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <i className="fas fa-balance-scale" style={{ fontSize: "28px", color: "#3b82f6" }} />
        </div>

        {error && (
          <div
            style={{
              fontSize: "14px",
              color: "#ef4444",
              marginBottom: "20px",
              padding: "12px 20px",
              background: "rgba(239, 68, 68, 0.1)",
              borderRadius: "8px",
              border: "1px solid rgba(239, 68, 68, 0.2)",
            }}
          >
            {error || t("noRecommendationsFoundTitle")}
          </div>
        )}

        <button
          onClick={fetchRecommendations}
          disabled={loading}
          style={{
            padding: "12px 32px",
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
            opacity: loading ? 0.6 : 1,
          }}
        >
          <i className="fas fa-download" style={{ marginRight: "8px" }} />
          {t("loadRecommendations")}
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 40px",
          background: "var(--card-bg)",
          borderRadius: "12px",
          border: "1px solid var(--card-border)",
          marginTop: "20px",
        }}
      >
        <i className="fas fa-spinner fa-spin" style={{ fontSize: "32px", color: "#3b82f6", marginBottom: "16px" }} />
        <div style={{ fontSize: "16px", color: "var(--card-text)" }}>{t("loadingRecommendations")}</div>
      </div>
    );
  }

  if (error && !recommendationDetails) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 40px",
          background: "var(--card-bg)",
          borderRadius: "12px",
          border: "1px solid var(--card-border)",
          marginTop: "20px",
          gap: "16px",
        }}
      >
        <div style={{ color: "#ef4444", fontWeight: 600 }}>{error}</div>
        <button
          onClick={fetchRecommendations}
          style={{
            padding: "10px 20px",
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          {t("retry")}
        </button>
      </div>
    );
  }

  if (!recommendationDetails || Object.keys(recommendationDetails).length === 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 40px",
          background: "var(--card-bg)",
          borderRadius: "12px",
          border: "1px solid var(--card-border)",
          marginTop: "20px",
          color: "var(--card-text)",
          fontWeight: 600,
        }}
      >
        {t("noRecommendationsAvailable")}
      </div>
    );
  }

  const { overall_summary, standards } = recommendationDetails;
  const computed = overall_summary?.computed_quick_stats || {};
  const inputStats = overall_summary?.input_quick_stats || {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "10px 0 40px" }}>
      <SectionCard title={t("overview")} icon={<i className="fas fa-balance-scale" />}>
        <div>
          <SubTitle>{t("computedQuickStats")}</SubTitle>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: "10px",
            }}
          >
            <StatTile label={t("totalTests")} value={formatNumber(computed.total_tests, 0, t)} />
            <StatTile
              label={t("failedTests")}
              value={formatNumber(computed.total_failed_tests, 0, t)}
              color="#dc2626"
              bg="rgba(239, 68, 68, 0.08)"
              border="rgba(239, 68, 68, 0.2)"
            />
            <StatTile
              label={t("passedTests")}
              value={formatNumber(computed.total_passed_tests, 0, t)}
              color="#16a34a"
              bg="rgba(34, 197, 94, 0.08)"
              border="rgba(34, 197, 94, 0.2)"
            />
            <StatTile
              label={t("standardsAnalyzed")}
              value={formatNumber(computed.standards_analyzed ?? standards?.length, 0, t)}
              color="#7c3aed"
              bg="rgba(139, 92, 246, 0.08)"
              border="rgba(139, 92, 246, 0.2)"
            />
            <StatTile
              label={t("readiness")}
              value={formatPercent(computed.overall_readiness_percent, t)}
              color="#d97706"
              bg="rgba(245, 158, 11, 0.08)"
              border="rgba(245, 158, 11, 0.2)"
            />
            <StatTile
              label={t("avgResponse")}
              value={formatSeconds(computed.overall_avg_response_time, t)}
              color="var(--muted-text)"
              bg="rgba(0, 0, 0, 0.04)"
              border="var(--card-border)"
            />
          </div>
        </div>

        <div>
          <SubTitle>{t("inputQuickStats")}</SubTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: "10px",
            }}
          >
            <StatTile label={t("totalTests")} value={formatNumber(inputStats.total_tests, 0, t)} />
            <StatTile
              label={t("failedTests")}
              value={formatNumber(inputStats.total_failed_tests, 0, t)}
              color="#dc2626"
              bg="rgba(239, 68, 68, 0.08)"
              border="rgba(239, 68, 68, 0.2)"
            />
            <StatTile
              label={t("highRisk")}
              value={formatNumber(inputStats.total_high_risk, 0, t)}
              color="#dc2626"
              bg="rgba(239, 68, 68, 0.08)"
              border="rgba(239, 68, 68, 0.2)"
            />
            <StatTile
              label={t("categoriesTested")}
              value={formatNumber(inputStats.total_categories_tested, 0, t)}
              color="#7c3aed"
              bg="rgba(139, 92, 246, 0.08)"
              border="rgba(139, 92, 246, 0.2)"
            />
            <StatTile
              label={t("readiness")}
              value={formatPercent(inputStats.overall_readiness, t)}
              color="#d97706"
              bg="rgba(245, 158, 11, 0.08)"
              border="rgba(245, 158, 11, 0.2)"
            />
            <StatTile
              label={t("avgResponse")}
              value={formatSeconds(inputStats.overall_avg_response_time, t)}
              color="var(--muted-text)"
              bg="rgba(0, 0, 0, 0.04)"
              border="var(--card-border)"
            />
          </div>
        </div>

        <div
          style={{
            background: "rgba(245, 158, 11, 0.08)",
            borderRadius: "12px",
            padding: "16px",
            border: "1px solid rgba(245, 158, 11, 0.2)",
          }}
        >
          <SubTitle>{t("validationWarnings")}</SubTitle>
          <div style={{ fontSize: "14px", color: "#64748b" }}>{t("na")}</div>
        </div>
      </SectionCard>

      <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {(standards || []).map((standard, idx) => (
          <StandardSection
            key={standard.standard_key || standard.standard || idx}
            standard={standard}
          />
        ))}
      </div>
    </div>
  );
};

export default RegulationComplianceRecs;
